import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { usePageCms } from "@/lib/siteSettings";

function resolveMediaUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${API}${url.slice(4)}`;
  return url;
}
import {
  Users, MessageSquare, HeartPulse, Trophy, Sparkles, ShieldCheck, ArrowRight,
  Dumbbell, Activity, UtensilsCrossed, Stethoscope, Star, Megaphone, Footprints, Zap,
  Clock, ExternalLink,
} from "lucide-react";

/**
 * Functional LAB Community — PUBLIC landing page.
 *
 * This is the promotional / informational face of the community shown to
 * logged-out visitors and to authenticated users who click "Community" in
 * the public header. It intentionally does NOT expose real posts, profiles
 * or the composer. The interactive community lives at:
 *   • /app/community        (patient dashboard)
 *   • /clinician/community  (clinician dashboard)
 */

const CATEGORY_ICONS = {
  general: MessageSquare, recovery: HeartPulse, "return-sport": Trophy, running: Footprints,
  "football-5": Zap, strength: Dumbbell, mobility: Activity, nutrition: UtensilsCrossed,
  "ask-clinician": Stethoscope, success: Star, announce: Megaphone,
};

const BENEFITS = [
  { icon: HeartPulse, title: "Real recovery stories",
    body: "Read how other Functional LAB members bounced back from injury, surgery and setbacks — and share your own." },
  { icon: Stethoscope, title: "Ask our clinicians",
    body: "Post a question in the Ask the Team space and get a verified answer from our qualified sport therapists." },
  { icon: Users, title: "Meet people like you",
    body: "Running club, 5-a-side football, S&C — small tribes inside the wider community keep the conversation focused and human." },
  { icon: Trophy, title: "Celebrate the wins",
    body: "Milestones, PBs and comebacks matter. The Success Stories feed exists to keep your streak visible and your community cheering." },
  { icon: Sparkles, title: "Curated, not chaotic",
    body: "Every post is moderated. No spam, no ads, no random self-promotion — just clinicians and patients pushing each other forward." },
  { icon: ShieldCheck, title: "Safe and private",
    body: "Members-only, moderated by our team. Anonymised handles are welcome. Nothing you share here is public on Google." },
];

const FAQS = [
  { q: "Who can join?",
    a: "Anyone with an active Functional LAB membership (Digital Support / Essential / Progress / Performance) or a one-off package purchase. Your clinician can also invite you if you're currently under our care." },
  { q: "Is the community really moderated?",
    a: "Yes — every new post is reviewed by a Functional LAB clinician before it goes live. This keeps the space clinically safe, respectful and free of misinformation." },
  { q: "Can I stay anonymous?",
    a: "You can use a display name of your choice. Your clinician always sees the real profile; other members only see your chosen display name." },
  { q: "Will clinicians actually reply?",
    a: "Our team drops in to the Ask the Team channel daily. Complex clinical cases are still handled in your private messages — the community is for general questions, encouragement and community." },
  { q: "What's not allowed?",
    a: "No unsolicited advice on someone else's injury (leave clinical calls to us), no advertising, no personal-attack language. Break the rules and the post is removed; repeat offences lose community access." },
];

const GUIDELINES = [
  "Be kind. Everyone here is somewhere on their recovery journey.",
  "Ask questions freely — there are no stupid ones.",
  "Share evidence, not urban myths.",
  "Respect privacy. Don't post identifying details of other members.",
  "Report anything uncomfortable — moderators triage within 24 h.",
];

export default function CommunityLanding() {
  const { t } = useTranslation();
  const cms = usePageCms("community");
  const [categories, setCategories] = useState([]);
  const [cmsCommunities, setCmsCommunities] = useState([]);
  const isAuthed = typeof window !== "undefined" && !!localStorage.getItem("fl_token");

  useEffect(() => {
    axios.get(`${API}/community/categories`).catch(() => ({ data: [] })).then((r) => setCategories(r?.data || []));
    axios.get(`${API}/public/cms/communities`).catch(() => ({ data: [] })).then((r) => setCmsCommunities(r?.data || []));
  }, []);

  return (
    <div className="min-h-screen fl-noise fl-page" style={{ background: "var(--fl-bg)", color: "var(--fl-text)" }}>
      <PublicHeader />

      {/* Hero */}
      {cms.isVisible("hero") && (
      <section className="pt-32 md:pt-40 pb-20 md:pb-24 px-6 bg-[#0A0A0A] fl-noise relative overflow-hidden">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("hero", "eyebrow", "Functional LAB Community · Members only")}</div>
            <h1 className="fl-heading text-4xl md:text-6xl leading-tight" data-testid="community-landing-h1">
              {cms.get("hero").heading ? cms.get("hero").heading : (<>Recovery doesn't happen<br />in isolation.</>)}
            </h1>
            <p className="mt-4 text-base md:text-lg max-w-xl" style={{ color: "var(--fl-muted)" }}>
              {cms.text("hero", "body", "A private, moderated community for Functional LAB patients and clinicians. Ask questions, share your progress, cheer each other on — a step back from the noise of open forums, curated by the people already treating you.")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {isAuthed ? (
                <Link to="/app/community" className="fl-btn" data-testid="community-cta-enter">
                  <ArrowRight size={16} /> Enter the community
                </Link>
              ) : (
                <>
                  <Link to="/register" className="fl-btn" data-testid="community-cta-join">
                    <ArrowRight size={16} /> Join Functional LAB
                  </Link>
                  <Link to="/login?next=/app/community" className="fl-btn-ghost" data-testid="community-cta-signin">
                    Sign in
                  </Link>
                </>
              )}
            </div>
            <p className="mt-4 text-xs" style={{ color: "var(--fl-muted-2)" }}>
              Available to every active membership and package client.
            </p>
          </div>
          <div className="fl-card p-6" data-testid="community-landing-stats">
            <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] px-2 py-0.5 rounded-full uppercase mb-3"
                 style={{ color: "var(--fl-orange)", background: "color-mix(in oklab, var(--fl-orange) 14%, transparent)", border: "1px solid color-mix(in oklab, var(--fl-orange) 30%, transparent)" }}>
              <Clock size={11} /> Launching soon
            </div>
            <h2 className="fl-heading text-2xl mb-2">Join the founding Functional LAB community.</h2>
            <p className="text-sm mb-4" style={{ color: "var(--fl-muted)" }}>
              We're seeding the space now with our first patients, clinicians and coaches. Early members shape the categories, the tone and the events — no fake stats, no bots, just the people already in the clinic.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { k: "safe", label: "Members-only", v: "Yes" },
                { k: "cost", label: "Cost to join", v: "£0" },
              ].map((s) => (
                <div key={s.k} className="p-3 rounded-md" style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)" }}>
                  <div className="fl-heading text-2xl" style={{ color: "var(--fl-orange)" }}>{s.v}</div>
                  <div className="fl-label mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Benefits */}
      {cms.isVisible("benefits") && (
      <section className="py-24 md:py-32 px-6 bg-[#121212] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="fl-label text-[color:var(--fl-orange)] mb-2">{cms.text("benefits", "eyebrow", "Why we built this")}</div>
          <h2 className="fl-heading text-3xl md:text-4xl mb-10">{cms.text("benefits", "heading", "Six reasons members stick around.")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {BENEFITS.map((b, i) => (
              <div key={i} className="fl-card p-5" data-testid={`community-benefit-${i}`}>
                <b.icon size={22} className="text-[color:var(--fl-orange)] mb-3" />
                <h3 className="fl-heading text-lg mb-1">{b.title}</h3>
                <p className="text-sm" style={{ color: "var(--fl-muted)" }}>{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Curated groups (CMS-driven) */}
      {cms.isVisible("groups") && cmsCommunities.length > 0 && (
        <section className="py-24 md:py-32 px-6 bg-[#0A0A0A] border-t border-white/5" data-testid="community-cms-groups">
          <div className="max-w-6xl mx-auto">
            <div className="fl-label text-[color:var(--fl-orange)] mb-2">{cms.text("groups", "eyebrow", "Curated groups")}</div>
            <h2 className="fl-heading text-3xl md:text-4xl mb-6">{cms.text("groups", "heading", "Communities inside Functional LAB.")}</h2>
            <p className="mb-8 text-sm md:text-base max-w-2xl" style={{ color: "var(--fl-muted)" }}>
              {cms.text("groups", "body", "Beyond the general feed, our clinicians curate focused member groups — a way to train, learn and recover with people on a similar path.")}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cmsCommunities.map((c) => {
                const isComingSoon = c.status === "coming_soon";
                return (
                  <div key={c.id} className="fl-card-elevated p-5 flex flex-col" data-testid={`community-cms-${c.id}`}>
                    {c.cover_image_url && (
                      <div className="rounded-md overflow-hidden mb-4 aspect-video bg-white/5">
                        <img src={resolveMediaUrl(c.cover_image_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="fl-heading text-lg">{c.name}</h3>
                      {isComingSoon && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider" style={{ color: "var(--fl-orange)", background: "color-mix(in oklab, var(--fl-orange) 14%, transparent)", border: "1px solid color-mix(in oklab, var(--fl-orange) 30%, transparent)" }}>
                          <Clock size={10} /> Coming soon
                        </span>
                      )}
                    </div>
                    {c.description && <p className="mt-2 text-sm flex-1" style={{ color: "var(--fl-muted)" }}>{c.description}</p>}
                    {c.tags?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {c.tags.map((tag) => (
                          <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full border" style={{ borderColor: "var(--fl-border-2)", color: "var(--fl-muted)" }}>#{tag}</span>
                        ))}
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-between">
                      {isComingSoon ? (
                        <span className="text-xs flex items-center gap-1" style={{ color: "var(--fl-orange)" }} data-testid={`community-cms-founding-${c.id}`}>
                          <Users size={12} /> Join the founding cohort
                        </span>
                      ) : (
                        <span className="text-xs flex items-center gap-1" style={{ color: "var(--fl-muted-2)" }}>
                          <Users size={12} /> {c.member_count || 0} members
                        </span>
                      )}
                      {!isComingSoon && c.external_url ? (
                        <a href={c.external_url} target="_blank" rel="noopener noreferrer" className="fl-btn-ghost text-xs px-3 py-1.5" data-testid={`community-cms-join-${c.id}`}>
                          Join <ExternalLink size={11} />
                        </a>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Category directory */}
      {cms.isVisible("categories") && (
      <section className="py-24 md:py-32 px-6 bg-[#121212] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="fl-label text-[color:var(--fl-orange)] mb-2">{cms.text("categories", "eyebrow", "Spaces")}</div>
          <h2 className="fl-heading text-3xl md:text-4xl mb-6">{cms.text("categories", "heading", "Small tribes, big impact.")}</h2>
          <p className="mb-8 text-sm md:text-base max-w-2xl" style={{ color: "var(--fl-muted)" }}>
            {cms.text("categories", "body", "Instead of one giant feed, the community is split into eleven focused spaces so the right people find your post — and so you find posts worth your time.")}
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="community-category-directory">
            {categories.map((c) => {
              const Icon = CATEGORY_ICONS[c.id] || MessageSquare;
              return (
                <div key={c.id} className="fl-card p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={16} className="text-[color:var(--fl-orange)]" />
                    <h3 className="fl-heading text-base">{c.label}</h3>
                  </div>
                  {c.description && (
                    <p className="text-xs leading-relaxed" style={{ color: "var(--fl-muted)" }}>{c.description}</p>
                  )}
                </div>
              );
            })}
            {categories.length === 0 && (
              <div className="text-sm" style={{ color: "var(--fl-muted-2)" }}>Loading spaces…</div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* Guidelines */}
      {cms.isVisible("guidelines") && (
      <section className="py-24 md:py-32 px-6 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          <div className="md:col-span-1">
            <div className="fl-label text-[color:var(--fl-orange)] mb-2">{cms.text("guidelines", "eyebrow", "House rules")}</div>
            <h2 className="fl-heading text-3xl md:text-4xl">{cms.text("guidelines", "heading", "Community guidelines.")}</h2>
            <p className="mt-3 text-sm" style={{ color: "var(--fl-muted)" }}>
              Signed by every member on joining. Enforced by our clinicians.
            </p>
          </div>
          <ul className="md:col-span-2 space-y-3" data-testid="community-guidelines">
            {GUIDELINES.map((g, i) => (
              <li key={i} className="fl-card p-4 flex items-start gap-3">
                <span className="fl-heading text-lg" style={{ color: "var(--fl-orange)" }}>{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm mt-1" style={{ color: "var(--fl-text)" }}>{g}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
      )}

      {/* FAQ */}
      {cms.isVisible("faq") && (
      <section className="py-24 md:py-32 px-6 bg-[#1A1A1A] border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="fl-label text-[color:var(--fl-orange)] mb-2">{cms.text("faq", "eyebrow", "Common questions")}</div>
          <h2 className="fl-heading text-3xl md:text-4xl mb-8">{cms.text("faq", "heading", "Before you join.")}</h2>
          <div className="space-y-3" data-testid="community-faqs">
            {FAQS.map((f, i) => (
              <details key={i} className="fl-card p-4">
                <summary className="fl-heading text-base cursor-pointer">{f.q}</summary>
                <p className="text-sm mt-3" style={{ color: "var(--fl-muted)" }}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Final CTA */}
      {cms.isVisible("finalCta") && (
      <section className="py-24 md:py-32 px-6 bg-gradient-to-b from-[#0A0A0A] via-[#170f0d] to-[#0A0A0A] border-t border-white/5" data-testid="community-landing-cta">
        <div className="max-w-3xl mx-auto text-center">
          <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("finalCta", "eyebrow", "Ready when you are")}</div>
          <h2 className="fl-heading text-3xl md:text-5xl mb-4">{cms.text("finalCta", "heading", "Join a community that's got your back.")}</h2>
          <p className="text-sm md:text-base mb-8" style={{ color: "var(--fl-muted)" }}>
            {cms.text("finalCta", "body", "The Functional LAB Community is free for every active member and package client. No new accounts, no upsell — just sign in and start talking.")}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {isAuthed ? (
              <Link to="/app/community" className="fl-btn">
                <ArrowRight size={16} /> Enter the community
              </Link>
            ) : (
              <>
                <Link to="/register" className="fl-btn">
                  <ArrowRight size={16} /> Create your account
                </Link>
                <Link to="/login?next=/app/community" className="fl-btn-ghost">Sign in</Link>
              </>
            )}
          </div>
        </div>
      </section>
      )}

      <PublicFooter />
    </div>
  );
}
